import { createContext, useContext, useState, useCallback } from 'react';
import { useSession } from './SessionContext';
import { useAuth } from './AuthContext';
import { usePlatform } from './PlatformContext';

const LearningContext = createContext(null);

// Thrown when a learning mutation is attempted without an active source.
// Components catch this and surface it via toast so the user knows why
// "Speichern" or "Deck erstellen" did nothing.
export class NoSourceSelectedError extends Error {
  constructor() {
    super('Bitte zuerst eine Quelle wählen: Codebase im IDE öffnen, Axiom-Token verbinden oder einen Catalog importieren.');
    this.name = 'NoSourceSelectedError';
  }
}

export function LearningProvider({ children }) {
  const { sessionId, workspacePath, sessionMode } = useSession();
  const { token, user } = useAuth();
  const { sources } = usePlatform();
  // Resolution order: explicit workspace > explicit session mode > tenant default
  // from platform config. The last tier covers the cloud-mode case where the
  // tenant admin stored axiom_token in tenant_settings but Layout's auto-session
  // hook hasn't fired yet (or raced with the user clicking "Als Lernkarten
  // speichern"). Without it, the chat answer would land via axiom but the deck
  // save would 400 with "workspace_path required".
  let effectiveWorkspace = workspacePath
    || (sessionMode === 'axiom' ? '__axiom__'
       : sessionMode === 'catalog' ? '__catalog__'
       : null);
  if (!effectiveWorkspace) {
    if (sources?.axiom?.enabled) effectiveWorkspace = '__axiom__';
    else if (sources?.documents?.enabled) effectiveWorkspace = '__catalog__';
  }
  const userId = user?.id || 'local';

  function authHeaders() {
    const h = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  }
  const [decks, setDecks] = useState([]);
  const [dueCount, setDueCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchDecks = useCallback(async () => {
    if (!effectiveWorkspace) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/learning/decks?workspace=${encodeURIComponent(effectiveWorkspace)}`);
      if (res.ok) {
        const data = await res.json();
        setDecks(data.decks);
      }
    } catch (err) {
      console.error('Failed to fetch decks:', err);
    } finally {
      setLoading(false);
    }
  }, [effectiveWorkspace]);

  const fetchDueCount = useCallback(async () => {
    try {
      const res = await fetch(`/api/learning/stats/due?userId=${userId}`, { headers: authHeaders() });
      if (res.ok) {
        const data = await res.json();
        setDueCount(data.total);
      }
    } catch (err) {
      console.error('Failed to fetch due count:', err);
    }
  }, []);

  const createDeck = useCallback(async (title, sourceType, sourceRef) => {
    if (!effectiveWorkspace) {
      throw new NoSourceSelectedError();
    }
    const res = await fetch('/api/learning/decks', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        title,
        source_type: sourceType,
        source_ref: sourceRef,
        workspace_path: effectiveWorkspace,
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to create deck');
    }
    const data = await res.json();
    setDecks(prev => [data.deck, ...prev]);
    return data.deck;
  }, [effectiveWorkspace, token]);

  const deleteDeck = useCallback(async (deckId) => {
    const res = await fetch(`/api/learning/decks/${deckId}`, { method: 'DELETE', headers: authHeaders() });
    if (!res.ok) throw new Error('Failed to delete deck');
    setDecks(prev => prev.filter(d => d.id !== deckId));
  }, [token]);

  const startDialogue = useCallback(async (pathId) => {
    const res = await fetch('/api/learning/dialogue', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ pathId, userId, sessionId }),
    });
    if (!res.ok) throw new Error('Failed to start dialogue');
    return res.json();
  }, [userId, sessionId]);

  const respondToDialogue = useCallback(async (dialogueId, answer) => {
    const res = await fetch(`/api/learning/dialogue/${dialogueId}/respond`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answer }),
    });
    if (!res.ok) throw new Error('Failed to submit response');
    return res.json();
  }, []);

  const createCard = useCallback(async (deckId, front, back, options = {}) => {
    const res = await fetch('/api/learning/cards', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({
        deck_id: deckId,
        front,
        back,
        card_type: options.card_type || 'basic',
        bloom_level: options.bloom_level || 'remember',
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to create card');
    }
    return res.json();
  }, [token]);

  const generateCards = useCallback(async (deckId, mode, target, cardCount) => {
    const res = await fetch(`/api/learning/decks/${deckId}/generate`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ mode, target, sessionId, cardCount }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to generate cards');
    }
    return res.json();
  }, [sessionId, token]);

  const startSession = useCallback(async (deckId, options = {}) => {
    const res = await fetch('/api/learning/sessions', {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ deckId, userId, workspace: effectiveWorkspace, ...options }),
    });
    if (!res.ok) throw new Error('Failed to start review session');
    return res.json();
  }, [effectiveWorkspace]);

  const submitReview = useCallback(async (reviewSessionId, cardId, rating, durationMs) => {
    const res = await fetch(`/api/learning/sessions/${reviewSessionId}/review`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ cardId, rating, durationMs }),
    });
    if (!res.ok) throw new Error('Failed to submit review');
    return res.json();
  }, [token]);

  const fetchStats = useCallback(async (period = '7d') => {
    const res = await fetch(`/api/learning/stats?userId=local&period=${period}`);
    if (!res.ok) throw new Error('Failed to fetch stats');
    return res.json();
  }, []);

  return (
    <LearningContext.Provider value={{
      decks, dueCount, loading,
      effectiveWorkspace, hasSource: !!effectiveWorkspace,
      fetchDecks, fetchDueCount, createDeck, deleteDeck,
      createCard, generateCards, startSession, submitReview, fetchStats,
      startDialogue, respondToDialogue,
    }}>
      {children}
    </LearningContext.Provider>
  );
}

export function useLearning() {
  const context = useContext(LearningContext);
  if (!context) throw new Error('useLearning must be used within LearningProvider');
  return context;
}
